import { BrandAssetDto, BrandAssetKind } from "@arrowsup/platform-dtos";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useBrandAssetLibrary } from "../../logic/brand-kit/use-brand-kit";
import { BrandAssetUploadButton } from "./brand-assets";

const kindLabels: Record<BrandAssetKind, { title: string; many: string }> = {
  [BrandAssetKind.Image]: { title: "Choose an image", many: "images" },
  [BrandAssetKind.Font]: { title: "Choose a font file", many: "font files" },
};

const Choice: React.FC<{ asset: BrandAssetDto; onPick: () => void }> = ({
  asset,
  onPick,
}) => (
  <ButtonBase
    onClick={onPick}
    aria-label={asset.name}
    sx={{
      flexDirection: "column",
      width: 160,
      p: 1,
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
    }}
  >
    {asset.previewUrl && (
      <Box
        component="img"
        src={asset.previewUrl}
        alt=""
        sx={{ width: "100%", height: 100, objectFit: "contain" }}
      />
    )}
    <Typography variant="caption" noWrap sx={{ maxWidth: "100%" }}>
      {asset.name}
    </Typography>
  </ButtonBase>
);

/** Picks an unarchived asset of one kind from the library, or uploads one. */
export const BrandAssetPicker: React.FC<{
  open: boolean;
  kind: BrandAssetKind;
  onClose: () => void;
  onPick: (asset: BrandAssetDto) => void;
}> = ({ open, kind, onClose, onPick }) => {
  const { data: library, isLoading, isError } = useBrandAssetLibrary();
  const labels = kindLabels[kind];
  const choices = (library ?? []).filter(
    (asset) => asset.kind === kind && !asset.archived
  );

  const pick = (asset: BrandAssetDto) => {
    onPick(asset);
    onClose();
  };

  const content = () => {
    if (isLoading) {
      return <CircularProgress />;
    }
    if (isError) {
      return <Alert severity="error">Failed to load the asset library</Alert>;
    }
    if (choices.length === 0) {
      return (
        <Typography color="text.secondary">
          {`No ${labels.many} in the library yet. Upload one.`}
        </Typography>
      );
    }
    return (
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {choices.map((asset) => (
          <Choice key={asset.id} asset={asset} onPick={() => pick(asset)} />
        ))}
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{labels.title}</DialogTitle>
      <DialogContent>{content()}</DialogContent>
      <DialogActions>
        <BrandAssetUploadButton kind={kind} onUploaded={pick} />
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
